/** BlueHQ's mark: a police-blue shield with a brass edge and "HQ" across it. Original artwork. */
export function BlueMark({ size = 40, className }: { size?: number; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 64 64" width={size} height={size} aria-hidden="true">
      <defs>
        <linearGradient id="bhq-shield" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2f5fb3" />
          <stop offset="100%" stopColor="#15295a" />
        </linearGradient>
      </defs>
      <path d="M32 3 L56 11 V30 C56 45 45 55 32 61 C19 55 8 45 8 30 V11 Z" fill="url(#bhq-shield)" stroke="#c9a24d" strokeWidth="3" strokeLinejoin="round" />
      <path d="M16 16 H48" stroke="#c9a24d" strokeWidth="1.6" strokeLinecap="round" opacity="0.8" />
      <text x="32" y="41" textAnchor="middle" fontFamily="'Public Sans', Arial, Helvetica, sans-serif" fontWeight="800" fontSize="22" letterSpacing="0.5" fill="#f4efe1">
        HQ
      </text>
    </svg>
  );
}

/** "BlueHQ" set in type: Blue regular, HQ heavy. */
export function BlueWordmark({ className }: { className?: string }) {
  return (
    <span className={`bhq-word ${className ?? ''}`}>
      Blue<b>HQ</b>
    </span>
  );
}

/** The six-point sheriff's star with a ball on each point (brass). Drawn as paths in a 24×24 box. */
export const STAR_PATH = 'M12.00 2.60 L14.45 7.76 L20.14 7.30 L16.90 12.00 L20.14 16.70 L14.45 16.24 L12.00 21.40 L9.55 16.24 L3.86 16.70 L7.10 12.00 L3.86 7.30 L9.55 7.76 Z';
export const STAR_BALLS: [number, number][] = [[12, 1.8], [20.83, 6.9], [20.83, 17.1], [12, 22.2], [3.17, 17.1], [3.17, 6.9]];

function StarShape({ id }: { id: string }) {
  return (
    <>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fbe6a2" />
          <stop offset="55%" stopColor="#d6a63c" />
          <stop offset="100%" stopColor="#9c7220" />
        </linearGradient>
      </defs>
      <path d={STAR_PATH} fill={`url(#${id})`} stroke="#6b4c12" strokeWidth="0.5" strokeLinejoin="round" />
      {STAR_BALLS.map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="1.65" fill={`url(#${id})`} stroke="#6b4c12" strokeWidth="0.45" />
      ))}
      <circle cx="12" cy="12" r="2.1" fill="none" stroke="#7a5716" strokeWidth="0.6" opacity="0.75" />
    </>
  );
}

/**
 * The BlueHQ logo as one vector drawing: chrome "Blue", police-blue "HQ", and a six-point ball-tipped gold
 * sheriff's star set in the Q. Scales to any size.
 */
export function BlueLogo({ className, width }: { className?: string; width?: number | string }) {
  return (
    <svg className={`bhq-logo-svg ${className ?? ''}`} viewBox="0 0 470 132" width={width} role="img" aria-label="BlueHQ">
      <defs>
        <linearGradient id="bhq-chrome" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="38%" stopColor="#e4e9f0" />
          <stop offset="52%" stopColor="#8d98a6" />
          <stop offset="70%" stopColor="#dfe5ec" />
          <stop offset="100%" stopColor="#a9b3bf" />
        </linearGradient>
        <linearGradient id="bhq-blue" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8cc0ff" />
          <stop offset="40%" stopColor="#3d8bff" />
          <stop offset="62%" stopColor="#1f63e6" />
          <stop offset="100%" stopColor="#1747b8" />
        </linearGradient>
        <filter id="bhq-glow" x="-10%" y="-20%" width="120%" height="140%">
          <feGaussianBlur stdDeviation="5" result="b" />
          <feFlood floodColor="#3d8bff" floodOpacity="0.35" />
          <feComposite in2="b" operator="in" />
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g transform="skewX(-10) translate(22 0)" fontFamily="'Public Sans', 'Arial Black', Arial, Helvetica, sans-serif" fontWeight="900" fontSize="112" letterSpacing="-3">
        <text x="6" y="104" textLength="250" lengthAdjust="spacingAndGlyphs" fill="url(#bhq-chrome)" stroke="rgb(255 255 255 / 0.25)" strokeWidth="1">
          Blue
        </text>
        <text x="262" y="104" textLength="168" lengthAdjust="spacingAndGlyphs" fill="url(#bhq-blue)" stroke="rgb(140 192 255 / 0.45)" strokeWidth="1" filter="url(#bhq-glow)">
          HQ
        </text>
      </g>
      <g transform="translate(386 42) scale(2)">
        <StarShape id="bhq-gold6" />
      </g>
    </svg>
  );
}

/** The star alone (icons, favicons). */
export function SheriffStar({ size = 24, className }: { size?: number; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      <StarShape id="bhq-gold6s" />
    </svg>
  );
}
